import { forwardRef, useEffect, useState } from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const FilePreview = forwardRef(({ file }, ref) => {
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const [fileType, setFileType] = useState("");
  const [textContent, setTextContent] = useState("");

  useEffect(() => {
    if (!file?.url) return;

    const extension = file.url.split(".").pop().toLowerCase().split("?")[0];
    setFileType(extension);
    setTextContent("");

    if (["txt", "js", "jsx", "json", "css", "html", "md"].includes(extension)) {
      fetch(file.url)
        .then((res) => res.text())
        .then(setTextContent)
        .catch(() => setTextContent("Unable to load file"));
    }
  }, [file]);

  if (!file?.url) {
    return <p className="p-3 text-muted">No file selected</p>;
  }

  /* ---------- IMAGE ---------- */
  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(fileType)) {
    return (
      <div className="p-3 text-center">
        <img src={file.url} alt="preview" className="w-100 shadow-sm" />
      </div>
    );
  }

  /* ---------- PDF ---------- */
  if (fileType === "pdf") {
    return (
      <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js">
        <Viewer fileUrl={file.url} plugins={[defaultLayoutPluginInstance]} />
      </Worker>
    );
  }

  /* ---------- VIDEO ---------- */
  if (["mp4", "webm", "ogg", "mov", "mkv"].includes(fileType)) {
    return (
      <video controls className="w-100 p-3">
        <source src={file.url} />
      </video>
    );
  }

  /* ---------- AUDIO ---------- */
  if (["mp3", "wav", "ogg", "m4a"].includes(fileType)) {
    return (
      <audio controls className="w-100 p-3">
        <source src={file.url} />
      </audio>
    );
  }

  /* ---------- TEXT ---------- */
  if (["txt", "js", "jsx", "json", "css", "html", "md"].includes(fileType)) {
    return (
      <pre style={{ padding: 16, whiteSpace: "pre-wrap" }}>
        {textContent || "No content"}
      </pre>
    );
  }

  /* ---------- DOC / PPT ---------- */
  if (["doc", "docx", "ppt", "pptx", "xls", "xlsx"].includes(fileType)) {
    const viewerUrl =
      fileType.includes("ppt") || fileType.includes("xls")
        ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
            file.url
          )}`
        : `https://docs.google.com/gview?url=${encodeURIComponent(
            file.url
          )}&embedded=true`;

    return (
      <iframe
        src={viewerUrl}
        title="Office Preview"
        width="100%"
        height="100%"
        style={{ border: "none" }}
      />
    );
  }

  /* ---------- FALLBACK ---------- */
  return (
    <div className="p-3 text-center">
      <p>Preview not available</p>
      <a href={file.url} target="_blank" rel="noreferrer">
        Download file
      </a>
    </div>
  );
});

export default FilePreview;
